import { Link, useLocation } from "react-router-dom";
import { useLocale } from "@/i18n/useLocale";
import { LOCALES, LOCALE_CODES, LOCALE_LABELS, type Locale } from "@/i18n/locales";
import { switchLocalePath } from "@/routes/manifest";
import { cx } from "@/lib/cx";

interface LanguageSwitcherProps {
  className?: string;
  /** Mobile bar passes -1 while the menu is closed, matching its other links. */
  tabIndex?: number;
}

/**
 * Renders one link per locale, each pointing at the COUNTERPART of the current
 * page rather than the home page — switching language on /de/platforms must
 * land on /platforms, not send the reader back to the start.
 *
 * The visible chip shows the two-letter code; the control's accessible name is
 * the endonym, so a screen reader announces "Deutsch" and never "D-E".
 *
 * Real links, not buttons: a language switch is a navigation to a different
 * URL, so it must be openable in a new tab and visible to a crawler following
 * the page — which is also what makes the hreflang set reachable.
 */
export default function LanguageSwitcher({
  className,
  tabIndex,
}: LanguageSwitcherProps): JSX.Element {
  const active = useLocale();
  const { pathname, hash } = useLocation();

  return (
    <div className={cx("flex items-center gap-1", className)}>
      {LOCALES.map((locale: Locale) => {
        const isActive = locale === active;
        return (
          <Link
            key={locale}
            // The hash is preserved: the hero's CTA is an in-page anchor, and a
            // language switch should not silently drop the reader's position.
            to={`${switchLocalePath(pathname, locale)}${hash}`}
            hrefLang={locale}
            aria-label={LOCALE_LABELS[locale]}
            aria-current={isActive ? "true" : undefined}
            tabIndex={tabIndex}
            className={cx(
              "font-mono text-[11px] tracking-[0.1em] px-2 py-1 rounded transition-colors",
              isActive ? "text-fg bg-bg-2" : "text-fg-dim hover:text-fg-muted",
            )}
          >
            {LOCALE_CODES[locale]}
          </Link>
        );
      })}
    </div>
  );
}
